import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import axiosInstance from '../../api/axiosConfig'; // Adjust path
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper, // Keep Paper for TableContainer
  Chip,
  Tooltip,
  // IconButton, // Removed as refresh is triggered by parent
  TablePagination,
  Fade,
  useTheme,
  Link,
  Skeleton // Import Skeleton
} from '@mui/material';
// import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'; // Moved to parent
// import RefreshIcon from '@mui/icons-material/Refresh'; // Moved to parent
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { format, parseISO } from 'date-fns';

const logger = {
  info: (...args) => console.log('[INFO]', ...args),
  error: (...args) => console.error('[ERROR]', ...args),
};

// Helper to format currency (adjust as needed)
const formatCurrency = (amount) => {
  if (amount === null || typeof amount === 'undefined' || isNaN(amount)) {
    return 'N/A';
  }
  return amount.toFixed(2);
};

function TransactionHistory({ schoolId, refreshTrigger }) {
  const theme = useTheme();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true); // Start loading initially
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchTransactions = useCallback(async () => {
    // Log entry and received props
    logger.info(`TransactionHistory: fetchTransactions called. schoolId: ${schoolId} (Type: ${typeof schoolId}), refreshTrigger: ${refreshTrigger}`);

    // **Crucial Check:** Verify schoolId *before* proceeding
    if (!schoolId || schoolId <= 0) {
      setError(null); // Clear previous errors if any
      setTransactions([]); // Clear transactions
      setLoading(false); // Stop loading state
      return; // Do not proceed
    }

    // Proceed only if schoolId is valid
    setLoading(true);
    setError(null);
    setTransactions([]); // Clear previous transactions before fetching new ones

    logger.info(`TransactionHistory: Fetching transactions for valid schoolId: ${schoolId}`);
    try {
      // Axios instance uses the header for schoolId
      const response = await axiosInstance.get('/api/transactions/school');
      setTransactions(response.data || []);
      logger.info(`TransactionHistory: Fetched ${response.data?.length || 0} transactions for schoolId ${schoolId}.`);
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message || 'Failed to load transaction history.';
      setError(errMsg);
      setTransactions([]); // Clear data on error
      logger.error(`TransactionHistory: Error fetching transactions for schoolId ${schoolId}:`, errMsg, err.response || err);
    } finally {
      setLoading(false);
    }
    // Dependencies: schoolId and refreshTrigger influence when fetchTransactions *could* be called via useEffect
  }, [schoolId, refreshTrigger]);

  // Effect to fetch data when schoolId or refreshTrigger changes
  useEffect(() => {
    logger.info(`TransactionHistory: useEffect triggered. schoolId: ${schoolId}, refreshTrigger: ${refreshTrigger}`);
    // The fetchTransactions function itself now contains the check for valid schoolId
    fetchTransactions();
    // fetchTransactions is memoized by useCallback and its dependencies are schoolId, refreshTrigger
  }, [fetchTransactions]);

  // --- Pagination Handlers ---
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const paginatedTransactions = transactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);


  // --- Render Logic ---
  const renderContent = () => {
    // Loading State: Show skeletons matching the table structure
    if (loading) {
      return (
        <Box sx={{ p: 2 }}>
            {/* Skeleton for Table Header */}
            <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
            {/* Skeleton for Table Rows */}
            {[...Array(rowsPerPage)].map((_, index) => (
                <Skeleton key={index} variant="rectangular" height={52} sx={{ mb: 0.5 }}/>
            ))}
             {/* Skeleton for Pagination */}
             <Skeleton variant="rectangular" height={52} sx={{ mt: 1}}/>
        </Box>
      );
    }

    // Error State
    if (error) {
      return (
        <Fade in={!!error}>
          <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ m: 2 }}>
            {error} - <Link component="button" variant="body2" onClick={fetchTransactions} sx={{ ml: 1 }}>Retry</Link>
          </Alert>
        </Fade>
      );
    }

    // No Data State
    if (transactions.length === 0) {
      return (
        <Typography sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
          No transaction history found for this account.
        </Typography>
      );
    }

    // --- Render Table ---
    return (
      <>
        {/* Use elevation={0} variant="outlined" if inside another Paper, or just TableContainer */}
        <TableContainer>
          <Table stickyHeader sx={{ minWidth: 650 }} aria-label="transaction history table">
            <TableHead> {/* Removed background color, rely on parent Paper */}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Date/Time</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Balance Before</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Balance After</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTransactions.map((row) => (
                <TableRow
                  hover // Add hover effect
                  key={row.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Tooltip title={row.createdAt ? format(parseISO(row.createdAt), 'PPPppp') : 'Invalid Date'}>
                      {/* Format date concisely */}
                      <span>{row.createdAt ? format(parseISO(row.createdAt), 'MMM d, yyyy HH:mm') : '-'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.transactionType}
                      size="small"
                      color={row.transactionType === 'CREDIT' ? 'success' : 'error'}
                      variant="outlined" // Keep outlined for contrast
                      sx={{ fontWeight: 'medium', textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: row.transactionType === 'CREDIT' ? theme.palette.success.dark : theme.palette.error.dark }}>
                    {row.transactionType === 'CREDIT' ? '+' : '-'} {formatCurrency(row.amount)}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}> {/* Limit width */}
                    <Tooltip title={row.description || 'No description'}>
                       <Typography variant="body2" noWrap> {/* noWrap enables ellipsis */}
                          {row.description || '-'}
                       </Typography>
                    </Tooltip>
                    </TableCell>
                  <TableCell align="right">{formatCurrency(row.balanceBefore)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium' }}>{formatCurrency(row.balanceAfter)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]} // Added 50
          component="div" // Use div for accessibility within Paper
          count={transactions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: `1px solid ${theme.palette.divider}`}} // Add separator
        />
      </>
    );
  };

  // --- Main Return ---
  // Removed outer Paper and Title/Refresh section (handled by parent)
  return (
    <Box>
      {renderContent()}
    </Box>
  );
}

TransactionHistory.propTypes = {
  schoolId: PropTypes.number, // Now allowing null/0 initially
  refreshTrigger: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default TransactionHistory;